import React from 'react';
import cn from 'classnames';
import {ComponentSize, StandardProps} from '..';
import FormControlContext from '../FormControl/FormControlContext';
import useFormControl from '../FormControl/useFormControl';
import formControlState from '../FormControl/formControlState';
import useForkRef from '../../hooks/useForkRef';

interface InputBaseRootProps extends StandardProps<React.HTMLAttributes<HTMLDivElement>> {
  size?: ComponentSize;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  outlined?: boolean;
  focused?: boolean;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
}

export const InputBaseRoot = React.forwardRef<HTMLDivElement, InputBaseRootProps>((props, ref) => {
  return (
    <div
      ref={ref}
      className={cn('input-base input-base__root', props.className, {
        'size-small': props.size === 'small',
        disabled: props.disabled,
        error: props.error,
        'full-width': props.fullWidth,
        outlined: props.outlined,
        focused: props.focused,
        'end-adornment': props.endAdornment,
        'start-adornment': props.startAdornment,
      })}
      style={props.style}
    >
      {props.children}
    </div>
  );
});

interface InputBaseComponentProps extends StandardProps<React.HTMLAttributes<HTMLInputElement>> {
  size?: ComponentSize;
  value?: string | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'password' | 'submit' | 'reset' | 'radio' | 'checkbox' | 'button' | 'file' | 'image';
}

export const InputBaseComponent = React.forwardRef<HTMLInputElement, InputBaseComponentProps>(({
  size,
  className,
  value: valueProp,
  ...props
}, ref) => {
  const value = valueProp === null ? '' : valueProp;
  return (
    <input
      ref={ref}
      value={value}
      className={cn('input-base input-base__input', className)}
      {...props}
    />
  );
});

export interface InputBaseProps extends StandardProps<React.HTMLAttributes<HTMLInputElement>> {
  inputRef?: React.Ref<HTMLInputElement>,
  inputProps?: InputBaseComponentProps,
  inputClassName?: string;
  size?: ComponentSize;
  placeholder?: string;
  disabled?: boolean;
  value?: string | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  fullWidth?: boolean;
  outlined?: boolean;
  autoComplete?: string;
  required?: boolean;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  type?: 'text' | 'password' | 'submit' | 'reset' | 'radio' | 'checkbox' | 'button' | 'file' | 'image';
  autoFocus?: boolean;
  maxLength?: number;
}

export const InputBase = React.forwardRef<HTMLDivElement, InputBaseProps>((props, ref) => {
  const {
    className,
    inputClassName,
    inputRef: inputRefProp,
    disabled,
    error,
    onChange,
    onBlur,
    onFocus,
    onClick,
    value,
    endAdornment,
    startAdornment,
    type = 'text',
    size,
    autoFocus = false,
    fullWidth,
    outlined,
    style,
    id,
    name,
    placeholder,
    autoComplete,
    required,
    children,
    inputProps,
    ...other
  } = props;

  const inputRef = React.useRef<HTMLInputElement>();
  const handleInputRef = useForkRef(inputRef, inputRefProp);

  const [focused, setFocused] = React.useState(false);
  const formControl = useFormControl();

  const fcState = formControlState({
    props,
    formControl,
    states: ['disabled', 'size'],
  });
  fcState.focused = formControl ? formControl.focused : focused;

  React.useEffect(() => {
    if (autoFocus) {
      inputRef?.current?.focus();
    }
  }, [autoFocus]);

  React.useEffect(() => {
    if (!formControl && disabled && focused) {
      setFocused(false);
    }
  }, [formControl, disabled, focused]);

  const handleFocus = (event) => {
    if (fcState.disabled) {
      event.stopPropagation();
      return;
    }

    onFocus?.(event);

    if (fcState && fcState.onFocus) {
      fcState.onFocus(event);
    } else {
      setFocused(true);
    }
  };

  const handleBlur = (event) => {
    onBlur?.(event);

    if (fcState && fcState.onBlur) {
      fcState.onBlur(event);
    } else {
      setFocused(false);
    }
  };

  const handleChange = (event) => {
    onChange?.(event);
  };

  const handleClick = (event) => {
    if (inputRef.current && event.currentTarget === event.target) {
      inputRef.current.focus();
    }

    onClick?.(event);
  };

  return (
    <InputBaseRoot
      className={className}
      ref={ref}
      onClick={handleClick}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      outlined={outlined}
      endAdornment={endAdornment}
      startAdornment={startAdornment}
      focused={focused}
      size={size}
    >
      {startAdornment}
      <FormControlContext.Provider value={null}>
        <InputBaseComponent
          ref={handleInputRef}
          className={inputClassName}
          style={style}
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          autoComplete={autoComplete}
          required={required}
          type={type}
          {...other}
          {...inputProps}
        />
        {children}
      </FormControlContext.Provider>
      {endAdornment}
    </InputBaseRoot>
  );
});

export default InputBase;
